<template>
  <div>
    <div class="card card-custom">
      <div class="card-body">
        <div class="row mb-3">
          <div class="col-md-6">
            <label class="form-label mb-1 small">Enter Search Values <span style="color:#888;">(one per line — Name, CNIC, NTN, GST, or City)</span></label>
            <textarea v-model="rawInput" class="form-control" rows="5" placeholder="e.g.&#10;35201-1234567-1&#10;AHMED ENTERPRISES&#10;1234567-8&#10;Lahore" :disabled="isSearching"></textarea>
            <div class="mt-1">
              <span class="badge" style="background:#2d5c88;color:#fff;">{{ parsedTokens.length }}</span>
              <span class="ms-1 small text-muted">value(s) entered</span>
            </div>
          </div>
          <div class="col-md-2 d-flex align-items-end">
            <button class="btn btn-primary" @click="startBulkSearch" :disabled="isSearching || parsedTokens.length === 0">
              <span v-if="!isSearching"><i class="fas fa-search"></i> Search</span>
              <span v-else><i class="fas fa-spinner fa-spin"></i> Searching...</span>
            </button>
          </div>
        </div>

        <!-- Progress Section -->
        <div v-if="isSearching || searchDone" class="border rounded p-3 bg-light mt-2">
          <div class="d-flex justify-content-between align-items-center mb-2">
            <strong>Search Progress</strong>
            <span class="badge" style="background:#2d5c88;color:#fff;font-size:0.85rem;">{{ overallPercent }}%</span>
          </div>

          <!-- Overall progress bar -->
          <div class="progress mb-3" style="height:12px;">
            <div class="progress-bar" role="progressbar" :style="{
                width: overallPercent + '%',
                background: overallPercent === 100 ? '#28a745' : '#2d5c88',
                transition: 'width 0.4s ease'
              }"></div>
          </div>

          <!-- Per-step cards -->
          <div class="row g-2">
            <div class="col-md-2" v-for="step in progressSteps" :key="step.key">
              <div class="border rounded p-2 text-center"
                   :style="{
                  borderColor: step.status === 'done' ? '#28a745' : step.status === 'active' ? '#2d5c88' : '#dee2e6',
                  background:  step.status === 'done' ? '#f0fbf3' : step.status === 'active' ? '#eaf3fb' : '#fff'
                }">
                <div style="font-size:1.1rem;">
                  <i v-if="step.status === 'done'" class="fas fa-check-circle" style="color:#28a745;"></i>
                  <i v-else-if="step.status === 'active'" class="fas fa-circle-notch fa-spin" style="color:#2d5c88;"></i>
                  <i v-else class="fas fa-circle" style="color:#ccc;"></i>
                </div>
                <div class="small fw-bold mt-1" style="color:#2d5c88;font-size:0.72rem;">{{ step.label }}</div>
                <div class="small text-muted" style="font-size:0.68rem;">
                  <span v-if="step.status === 'done'">{{ step.found }} found</span>
                  <span v-else-if="step.status === 'active'">Searching...</span>
                  <span v-else>Waiting</span>
                </div>
                <div class="progress mt-1" style="height:4px;">
                  <div class="progress-bar"
                       :style="{
                      width: step.percent + '%',
                      background: step.status === 'done' ? '#28a745' : '#2d5c88'
                    }"></div>
                </div>
              </div>
            </div>
          </div>

          <div v-if="searchDone" class="alert alert-success mt-3 mb-0 py-2">
            <i class="fas fa-check-double me-2"></i>
            All {{ parsedTokens.length }} value(s) searched &mdash; {{ totalFound }} total match(es) found
          </div>
        </div>

        <div v-if="searchDone && totalFound === 0" class="alert alert-warning mt-3">
          No record found.
        </div>
      </div>
    </div>

    <!-- Client Profile -->
    <div class="card mt-3" v-if="results.clientProfile.length > 0">
      <div class="card-header"><h5>Client Profile</h5></div>
      <div class="card-body">
        <table class="table table-sm table-bordered" id="bulkClientTable">
          <thead class="bg-custom">
            <tr>
              <th>Matched By</th>
              <th>Client Code</th>
              <th>Name</th>
              <th>Partner Type</th>
              <th>City</th>
              <th>Business Nature</th>
              <th>Address</th>
              <th>Mobile No</th>
              <th>CNIC</th>
              <th>GST</th>
              <th>NTN</th>
              <th>Active</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in results.clientProfile" :key="item.BP_ID + '_' + item._matchedBy">
              <td><span class="badge" style="background:#eaf3fb;color:#2d5c88;border:1px solid #bdd0e4;font-size:0.72rem;">{{ item._matchedBy }}</span></td>
              <td>{{ item.BP_CODE_NEW }}</td>
              <td>{{ item.BP_DSC }}</td>
              <td>{{ item.PTR_TYPE }}</td>
              <td>{{ item.City }}</td>
              <td>{{ item.BN_DESC }}</td>
              <td>{{ item.Addresses }}</td>
              <td>{{ item.BP_MOBILE }}</td>
              <td>{{ item.BP_NIC_NO }}</td>
              <td>{{ item.BP_GST_NO }}</td>
              <td>{{ item.BP_NTN_NO }}</td>
              <td><input type="checkbox" :checked="item.BP_ACTIVE == 1" disabled></td>
              <td>
                <button class="btn btn-primary btn-sm" @click="editInsured(item.BP_ID, item.PTR_ID)"><i class="fas fa-pencil-alt" /></button>
                <button class="btn btn-success btn-sm" @click="printKyc(item.BP_ID, item.PTR_ID)"><i class="fas fa-download" /></button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Director Info -->
    <div class="card mt-3" v-if="results.directorInfo.length > 0">
      <div class="card-header"><h5>Director Info</h5></div>
      <div class="card-body">
        <table class="table table-sm table-bordered" id="bulkDirectorTable">
          <thead class="bg-custom">
            <tr>
              <th>Matched By</th>
              <th>Client Code</th>
              <th>Client Name</th>
              <th>Director Name</th>
              <th>Address</th>
              <th>CNIC</th>
              <th>Issue Date</th>
              <th>Expiry Date</th>
              <th>PEP</th>
              <th>Self Position</th>
              <th>Association</th>
              <th>Status Date</th>
              <th>Active</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in results.directorInfo" :key="item.BP_CODE_NEW + '_' + item.BO_NAME + '_' + item._matchedBy">
              <td><span class="badge" style="background:#eaf3fb;color:#2d5c88;border:1px solid #bdd0e4;font-size:0.72rem;">{{ item._matchedBy }}</span></td>
              <td>{{ item.BP_CODE_NEW }}</td>
              <td>{{ item.BP_DSC }}</td>
              <td>{{ item.BO_NAME }}</td>
              <td>{{ item.BO_ADDRESS }}</td>
              <td>{{ item.BO_CNICPASSNO }}</td>
              <td>{{ item.BO_CNICPASS_ISSUEDTE }}</td>
              <td>{{ item.BO_CNICPASS_EXPDTE }}</td>
              <td>{{ item.BO_PEPYN }}</td>
              <td>{{ item.BO_IFYESSELFPOSITION }}</td>
              <td>{{ item.BO_ASSOCRELWITHPEP }}</td>
              <td>{{ item.BO_STATUSDATE }}</td>
              <td><input type="checkbox" :checked="item.BO_ACTIVE == 1" disabled></td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Beneficial Ownership -->
    <div class="card mt-3" v-if="results.beneficialOwnership.length > 0">
      <div class="card-header"><h5>Beneficial Ownership</h5></div>
      <div class="card-body">
        <table class="table table-sm table-bordered" id="bulkBeneficialTable">
          <thead class="bg-custom">
            <tr>
              <th>Matched By</th>
              <th>Client Code</th>
              <th>Client Name</th>
              <th>Beneficiary Name</th>
              <th>Address</th>
              <th>CNIC</th>
              <th>Issue Date</th>
              <th>Expiry Date</th>
              <th>PEP</th>
              <th>Self Position</th>
              <th>Association</th>
              <th>Active</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in results.beneficialOwnership" :key="item.BP_CODE_NEW + '_' + item.BO_NAME + '_' + item._matchedBy">
              <td><span class="badge" style="background:#eaf3fb;color:#2d5c88;border:1px solid #bdd0e4;font-size:0.72rem;">{{ item._matchedBy }}</span></td>
              <td>{{ item.BP_CODE_NEW }}</td>
              <td>{{ item.BP_DSC }}</td>
              <td>{{ item.BO_NAME }}</td>
              <td>{{ item.BO_ADDRESS }}</td>
              <td>{{ item.BO_CNICPASSNO }}</td>
              <td>{{ item.BO_CNICPASS_ISSUEDTE }}</td>
              <td>{{ item.BO_CNICPASS_EXPDTE }}</td>
              <td>{{ item.BO_PEPYN }}</td>
              <td>{{ item.BO_IFYESSELFPOSITION }}</td>
              <td>{{ item.BO_ASSOCRELWITHPEP }}</td>
              <td><input type="checkbox" :checked="item.BO_ACTIVE == 1" disabled></td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Signatory -->
    <div class="card mt-3" v-if="results.signatoryInfo.length > 0">
      <div class="card-header"><h5>Signatory</h5></div>
      <div class="card-body">
        <table class="table table-sm table-bordered" id="bulkSignatoryTable">
          <thead class="bg-custom">
            <tr>
              <th>Matched By</th>
              <th>Client Code</th>
              <th>Client Name</th>
              <th>Signatory Name</th>
              <th>CNIC</th>
              <th>Designation</th>
              <th>BOR</th>
              <th>Status</th>
              <th>Active</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in results.signatoryInfo" :key="item.BP_CODE_NEW + '_' + item.BO_NAME + '_' + item._matchedBy">
              <td><span class="badge" style="background:#eaf3fb;color:#2d5c88;border:1px solid #bdd0e4;font-size:0.72rem;">{{ item._matchedBy }}</span></td>
              <td>{{ item.BP_CODE_NEW }}</td>
              <td>{{ item.BP_DSC }}</td>
              <td>{{ item.BO_NAME }}</td>
              <td>{{ item.BO_CNICPASSNO }}</td>
              <td>{{ item.BO_DESIGNATION }}</td>
              <td>{{ item.BO_BORYN }}</td>
              <td>{{ item.BO_STATUS }}</td>
              <td><input type="checkbox" :checked="item.BO_ACTIVE == 1" disabled></td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Block Accounts Client -->
    <div class="card mt-3" v-if="results.blockAccountsClient.length > 0">
      <div class="card-header"><h5>Block Accounts Client</h5></div>
      <div class="card-body">
        <table class="table table-sm table-bordered" id="bulkBlockTable">
          <thead class="bg-custom">
            <tr>
              <th>Matched By</th>
              <th>Client Name</th>
              <th>Father Name</th>
              <th>CNIC No</th>
              <th>SRO NO</th>
              <th>DATE OF SCR</th>
              <th>Black List By</th>
              <th>SECP Email Date</th>
              <th>Status</th>
              <!--<th>Active</th>-->
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in results.blockAccountsClient" :key="item.SRO_NO + '_' + item._matchedBy">
              <td><span class="badge" style="background:#fff0f0;color:#c0392b;border:1px solid #f5b7b1;font-size:0.72rem;">{{ item._matchedBy }}</span></td>
              <td>{{item.CLIENT_NAME}}</td>
              <td>{{item.FATHERNAME}}</td>
              <td>{{item.CNIC_NO}}</td>
              <td>{{item.SRO_NO}}</td>
              <td>{{ formatDate(item.DATE_OF_SCR) }}</td>
              <td>{{item.BLACK_LIST_BY}}</td>
              <td>{{ formatDate(item.SECP_EMAIL_DATE) }}</td>
              <td>{{item.STATUS}}</td>
              <!--<td><input type="checkbox" :checked="item.ACTIVE == 1" disabled></td>-->
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- CDC Shareholders -->
    <div class="card mt-3" v-if="results.cdcShareholders.length > 0">
      <div class="card-header"><h5>CDC Shareholders</h5></div>
      <div class="card-body">
        <table class="table table-sm table-bordered" id="bulkCdcTable">
          <thead class="bg-custom">
            <tr>
              <th>Matched By</th>
              <th>Folio No</th>
              <th>Name</th>
              <th>Address</th>
              <th>Share Held</th>
              <th>CNIC</th>
              <th>Mobile</th>
              <th>Email</th>
              <th>CDC</th>
              <th>As On Date</th>
              <th>Insert Date</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in results.cdcShareholders" :key="item.FOLIO_NO + '_' + item.CNIC_NO + '_' + item._matchedBy">
              <td><span class="badge" style="background:#eaf3fb;color:#2d5c88;border:1px solid #bdd0e4;font-size:0.72rem;">{{ item._matchedBy }}</span></td>
              <td>{{ item.FOLIO_NO }}</td>
              <td>{{ item.NAME }}</td>
              <td>{{ item.ADDRESS }}</td>
              <td>{{ formatNumber(item.SHARE_HELD) }}</td>
              <td>{{ item.CNIC_NO }}</td>
              <td>{{ item.MOBILE_NO }}</td>
              <td>{{ item.EMAIL_ADDRESS }}</td>
              <td>{{ item.CDC_YN }}</td>
              <td>{{ formatDate(item.AS_ON_DATE) }}</td>
              <td>{{ formatDate(item.INSERT_DATE) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

  </div>
</template>

<script>
  import axios from 'axios';

  var STEPS = [
    { key: 'clientProfile', label: 'Client Profile', apiKey: 'clientProfile', tableId: 'bulkClientTable' },
    { key: 'directorInfo', label: 'Director Info', apiKey: 'directorInfo', tableId: 'bulkDirectorTable' },
    { key: 'beneficialOwnership', label: 'Beneficial Ownership', apiKey: 'beneficialOwnership', tableId: 'bulkBeneficialTable' },
    { key: 'signatoryInfo', label: 'Signatory Info', apiKey: 'signatoryInfo', tableId: 'bulkSignatoryTable' },
    { key: 'blockAccountsClient', label: 'Block Accounts', apiKey: 'blockAccountsClient', tableId: 'bulkBlockTable' },
    { key: 'cdcShareholders', label: 'CDC Shareholders', apiKey: 'cdcShareholders', tableId: 'bulkCdcTable' }  
  ];

  export default {
    data() {
      return {
        rawInput: '',
        isSearching: false,
        searchDone: false,
        progressSteps: [],
        results: {
          clientProfile: [],
          directorInfo: [],
          beneficialOwnership: [],
          signatoryInfo: [],
          blockAccountsClient: [],
          cdcShareholders: []
        }
      };
    },

    computed: {
      parsedTokens() {
        return this.rawInput
          .split('\n')
          .map(function (t) { return t.trim(); })
          .filter(function (t) { return t.length > 0; });
      },
      overallPercent() {
        if (!this.progressSteps.length) return 0;
        var total = 0;
        for (var i = 0; i < this.progressSteps.length; i++) {
          total += this.progressSteps[i].percent;
        }
        return Math.round(total / this.progressSteps.length);
      },
      totalFound() {
        var count = 0;
        var keys = Object.keys(this.results);
        for (var i = 0; i < keys.length; i++) {
          count += this.results[keys[i]].length;
        }
        return count;
      }
    },

    methods: {

      startBulkSearch() {
        var self = this;
        var tokens = self.parsedTokens;
        if (!tokens.length) return;

        self.isSearching = true;
        self.searchDone = false;

        self.results = {
          clientProfile: [],
          directorInfo: [],
          beneficialOwnership: [],
          signatoryInfo: [],
          blockAccountsClient: [],
          cdcShareholders: []
        };

        STEPS.forEach(function (s) {
          if ($.fn.DataTable.isDataTable('#' + s.tableId)) {
            $('#' + s.tableId).DataTable().destroy();
          }
        });

        self.progressSteps = STEPS.map(function (s) {
          return {
            key: s.key,
            label: s.label,
            apiKey: s.apiKey,
            tableId: s.tableId,
            status: 'pending',
            percent: 0,
            found: 0
          };
        });

        self._runStep(0, tokens);
      },

      _runStep(stepIndex, tokens) {
        var self = this;
        if (stepIndex >= STEPS.length) {
          self.isSearching = false;
          self.searchDone = true;
          return;
        }

        var step = self.progressSteps[stepIndex];
        step.status = 'active';

        self._runToken(stepIndex, tokens, 0, function () {
          step.status = 'done';
          step.percent = 100;
          step.found = self.results[step.apiKey].length;

          self.$nextTick(function () {
            if ($('#' + step.tableId).length) {
              $('#' + step.tableId).DataTable({
                destroy: true,
                responsive: true,
                language: { emptyTable: 'No data available' }
              });
            }
            self._runStep(stepIndex + 1, tokens);
          });
        });
      },

      _runToken(stepIndex, tokens, tokenIndex, onAllDone) {
        var self = this;
        var step = self.progressSteps[stepIndex];
        var total = tokens.length;

        if (tokenIndex >= total) {
          onAllDone();
          return;
        }

        var token = tokens[tokenIndex];

        axios.get('/api/UICApi/ClientAdvanceSearch', { params: { search: token } })
          .then(function (res) {
            var data = res.data || {};
            var apiKey = STEPS[stepIndex].apiKey;
            var incoming = data[apiKey] || [];

            self._mergeResults(apiKey, incoming, token);

            step.percent = Math.round(((tokenIndex + 1) / total) * 100);
            self._runToken(stepIndex, tokens, tokenIndex + 1, onAllDone);
          })
          .catch(function () {
            step.percent = Math.round(((tokenIndex + 1) / total) * 100);
            self._runToken(stepIndex, tokens, tokenIndex + 1, onAllDone);
          });
      },

      _mergeResults(apiKey, incoming, token) {
        var self = this;
        var existing = self.results[apiKey];
        var i, j, k, copy;

        if (apiKey === 'clientProfile') {
          var seenIds = {};
          for (i = 0; i < existing.length; i++) seenIds[existing[i].BP_ID] = true;
          for (j = 0; j < incoming.length; j++) {
            if (!seenIds[incoming[j].BP_ID]) {
              seenIds[incoming[j].BP_ID] = true;
              copy = Object.assign({}, incoming[j]);
              copy._matchedBy = token;
              existing.push(copy);
            }
          }

        } else if (apiKey === 'blockAccountsClient') {
          var seenSr = {};
          for (i = 0; i < existing.length; i++) seenSr[existing[i].SRO_NO] = true;
          for (j = 0; j < incoming.length; j++) {
            if (!seenSr[incoming[j].SRO_NO]) {
              seenSr[incoming[j].SRO_NO] = true;
              copy = Object.assign({}, incoming[j]);
              copy._matchedBy = token;
              existing.push(copy);
            }
          }

        } else if (apiKey === 'cdcShareholders') {
          var seenCdc = {};
          for (i = 0; i < existing.length; i++) {
            seenCdc[existing[i].FOLIO_NO + '|' + existing[i].CNIC_NO] = true;
          }
          for (j = 0; j < incoming.length; j++) {
            k = incoming[j].FOLIO_NO + '|' + incoming[j].CNIC_NO;
            if (!seenCdc[k]) {
              seenCdc[k] = true;
              copy = Object.assign({}, incoming[j]);
              copy._matchedBy = token;
              existing.push(copy);
            }
          }

        } else {
          var seenKeys = {};
          for (i = 0; i < existing.length; i++) {
            k = existing[i].BP_CODE_NEW + '|' + existing[i].BO_NAME + '|' + existing[i].BO_CNICPASSNO;
            seenKeys[k] = true;
          }
          for (j = 0; j < incoming.length; j++) {
            k = incoming[j].BP_CODE_NEW + '|' + incoming[j].BO_NAME + '|' + incoming[j].BO_CNICPASSNO;
            if (!seenKeys[k]) {
              seenKeys[k] = true;
              copy = Object.assign({}, incoming[j]);
              copy._matchedBy = token;
              existing.push(copy);
            }
          }
        }
      },

      formatDate(date) {
        return date ? new Date(date).toISOString().split('T')[0] : '';
      },
      formatNumber(value) {
        return value != null ? Number(value).toLocaleString() : '';
      },
      editInsured(id, ptrId) {
        if (ptrId === 17 || ptrId === 1) {
          window.open('/uic/IndividualProfile?id=' + id);
        } else {
          window.open('/uic/CorporateProfile?id=' + id);
        }
      },

      printKyc(id, ptrId) {
        if (ptrId === 17 || ptrId === 1) {
          window.open('/UIC/IndividualClientPrint?id=' + id);
        } else {
          window.open('/UIC/CorporateClientPrint?id=' + id);
        }
      }
    }
  };
</script>

<style scoped>
  h5 {
    font-size: 1.5rem;
    color: #333;
    margin-bottom: 15px;
    font-weight: 600;
    border-bottom: 2px solid #2d5c88;
    padding-bottom: 5px;
  }
</style>



// WEBPACK FOOTER //
// src/components/ims/MatchingBulkClient.vue